"use client";

import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const staffNotes = [{
  snippet: "A gentle, playful soul",
  staffer: "Elena",
  rotation: "-1.5deg"
}, {
  snippet: "Pure velvet elegance",
  staffer: "Marcus",
  rotation: "1.2deg"
}, {
  snippet: "The sweetest companion",
  staffer: "Sofia",
  rotation: "-1deg"
}, {
  snippet: "Perfect for cozy cuddles",
  staffer: "Julian",
  rotation: "1.5deg"
}, {
  snippet: "Rare and truly regal",
  staffer: "Marcus",
  rotation: "-0.8deg"
}, {
  snippet: "A bundle of pure joy",
  staffer: "Elena",
  rotation: "1deg"
}];
const cardRotations = ["md:-rotate-[1.2deg]", "md:rotate-[0.8deg]", "md:-rotate-[0.5deg]", "md:rotate-[1deg]", "md:-rotate-[0.8deg]", "md:rotate-[1.2deg]"];
function TreasuresConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[420px] rounded-lg bg-card/60 animate-pulse" style={{
        borderColor: "var(--border)"
      }} />)}
      </div>;
  }
  if (error) {
    return <p className="text-center" style={{
      color: "var(--muted-foreground)"
    }}>
        Our nursery is currently updating. Please check back shortly.
      </p>;
  }
  if (products.length === 0) {
    return <p className="text-center" style={{
      color: "var(--muted-foreground)"
    }}>
        No kittens are currently available. Please join our waitlist.
      </p>;
  }
  const displayed = products.slice(0, 6);
  return <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-3">
      {displayed.map((product, i) => {
      const note = staffNotes[i % staffNotes.length];
      const rotation = cardRotations[i % cardRotations.length];
      const category = product.categories?.[0]?.name;
      return <article key={product.id} data-index={i} className={`relative flex flex-col p-6 rounded-lg border bg-card transition-all duration-300 ease-out hover:rotate-0 hover:shadow-lg group ${rotation}`} style={{
        borderColor: "var(--border)",
        transitionTimingFunction: "cubic-bezier(.2,.7,.2,1)"
      }}>
            <Link to={product.link} className="block">
              <div className="overflow-hidden rounded-lg border mb-5" style={{
            borderColor: "var(--border)"
          }}>
                <div className="aspect-[3/4] bg-muted overflow-hidden relative">
                  {product.discounted_price !== undefined && <div className="absolute top-4 -right-6 w-28 bg-[var(--primary)] text-white text-center text-xs font-bold py-1 rotate-45 z-10 shadow-sm">
                      Special
                    </div>}
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /> : <div className="h-full w-full flex items-center justify-center" style={{
                backgroundColor: "var(--muted)",
                color: "var(--muted-foreground)",
                fontFamily: "var(--font-serif)"
              }}>
                      <span className="text-4xl">{"\u2764"}</span>
                    </div>}
                </div>
              </div>
            </Link>

            {category && <Badge variant="outline" className="mb-3 self-start" style={{
          fontFamily: "var(--font-mono)",
          color: "var(--secondary)",
          borderColor: "var(--secondary)",
          backgroundColor: "transparent",
          fontSize: "0.95rem",
          fontWeight: 500,
          paddingLeft: "0.625rem",
          paddingRight: "0.625rem"
        }}>
                {category}
              </Badge>}

            <Link to={product.link}>
              <h3 className="font-default text-xl md:text-2xl font-medium leading-tight mb-1.5 relative inline-block" style={{
            fontFamily: "var(--font-serif)",
            color: "var(--foreground)",
            letterSpacing: "-0.012em"
          }}>
                <span className="relative">
                  {product.title}
                  <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1 h-[2px] origin-left scale-x-0 transition-transform duration-[320ms] ease-out group-hover:scale-x-100" style={{
                backgroundColor: "var(--primary)"
              }} />
                </span>
              </h3>
            </Link>

            {product.short_description && <div className="text-sm mb-5 [&_p]:mb-0 [&_p:last-child]:mb-0 line-clamp-2" style={{
          color: "var(--muted-foreground)"
        }} dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}

            {product.tags && product.tags.length > 0 && <div className="flex flex-wrap gap-2 mb-5">
                {product.tags.slice(0, 4).map(tag => <Badge key={tag.id} variant="outline" className="text-xs" style={{
            color: "var(--muted-foreground)",
            borderColor: "var(--border)",
            backgroundColor: "var(--muted)"
          }}>
                    {tag.name}
                  </Badge>)}
              </div>}

            <div className="mb-5 pl-3 border-l-2" style={{
          borderColor: "var(--primary)",
          transform: `rotate(${note.rotation})`
        }}>
              <p className="leading-snug" style={{
            fontFamily: "var(--font-mono)",
            color: "var(--muted-foreground)",
            fontSize: "1.125rem"
          }}>
                {note.snippet} — {note.staffer}
              </p>
            </div>
          </article>;
    })}
    </div>;
}
export default function Kittengallery() {
  return <section data-section-id="12012" id="kittengallery" className="relative py-24 md:py-32 bg-background text-foreground">
      <div className="absolute top-0 left-0 right-0 flex justify-center px-6 md:px-24 pointer-events-none">
        <svg viewBox="0 0 1200 24" preserveAspectRatio="none" className="w-full max-w-6xl h-6" aria-hidden="true">
          <path d="M0,12 C100,4 200,20 300,12 C400,4 500,20 600,12 C700,4 800,20 900,12 C1000,4 1100,20 1200,12" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mx-auto max-w-[720px] text-center mb-16 md:mb-20">
          <p className="font-default uppercase mb-5" style={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          letterSpacing: "0.14em",
          color: "var(--muted-foreground)"
        }}>
            <Link to="#" className="underline underline-offset-4 hover:text-primary transition-colors cursor-pointer">Available Kittens</Link>
          </p>

          <h2 className="font-default mb-6" style={{
          fontFamily: "var(--font-serif)",
          fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
          fontWeight: 500,
          lineHeight: 1.08,
          letterSpacing: "-0.018em",
          color: "var(--foreground)"
        }}>
            {"Meet our "}
            <span className="relative inline-block">
              <span className="relative z-10 !text-[rgba(90,74,64,1)]">PAWpanions</span>
              <svg viewBox="0 0 200 28" preserveAspectRatio="none" className="absolute left-0 right-0 -bottom-1 w-full h-3 md:h-4" aria-hidden="true">
                <path d="M4,18 C40,8 90,22 130,14 C160,8 185,16 196,12" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </span>
            {"!"}
          </h2>

          <p className="font-default" style={{
          fontSize: "clamp(1.0625rem, 1.05vw, 1.1875rem)",
          lineHeight: 1.6,
          color: "var(--muted-foreground)"
        }}>
            Rare, hand-raised exotic kittens waiting to find their forever homes with you.
          </p>

          <p className="font-default mt-3" style={{
          fontSize: "clamp(0.9375rem, 0.95vw, 1rem)",
          lineHeight: 1.5,
          color: "var(--muted-foreground)"
        }}>
            Click photo to find out more!
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc"
      }}>
          <TreasuresConsumer />
        </EcommerceProductsProvider>

        <div className="mt-16 md:mt-20 flex justify-center">
          <Link to="/SearchAvailableKittensPage" className="group inline-flex items-center gap-2 font-default text-base font-medium" style={{
          color: "var(--foreground)"
        }}><span className="relative">
              View all available kittens
              <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1 h-[1.5px] transition-all duration-200 group-hover:h-[2.5px]" style={{
              backgroundColor: "var(--primary)"
            }} />
            </span>
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" style={{
            color: "var(--primary)"
          }} /></Link>
        </div>
      </div>
    </section>;
}